import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Upload, Download, Sparkles } from "lucide-react";
import { DataCleanupTable } from "./DataCleanupTable";

interface CleanedRecord {
  tax_id: string;
  date_recorded: string;
  instrument_type: string;
  county: string;
  original_name: string;
  first_name: string;
  last_name: string;
  seller_address: string;
  seller_city: string;
  seller_state: string;
  seller_zip: string;
  original_data?: string;
  issues_found?: string[];
}

export default function DataCleanupPage() {
  const [rawData, setRawData] = useState("");
  const [cleanedData, setCleanedData] = useState<CleanedRecord[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const { toast } = useToast();

  const parseTabDelimitedData = (text: string): any[] => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split('\t').map(h => h.trim().toLowerCase().replace(/ /g, '_'));
    const records = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split('\t');
      if (values.length === headers.length) {
        const record: any = {};
        headers.forEach((header, idx) => {
          record[header] = values[idx]?.trim() || '';
        });
        records.push(record);
      }
    }

    return records;
  };

  const cleanupData = async () => {
    if (!rawData.trim()) {
      toast({
        title: "No data provided",
        description: "Please paste your data first",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    setStatusMessage("Parsing your data...");
    setCleanedData([]);
    try {
      const parsedRecords = parseTabDelimitedData(rawData);
      
      if (parsedRecords.length === 0) {
        toast({
          title: "Parse Error",
          description: "Could not parse the data. Make sure it's tab-delimited.",
          variant: "destructive",
        });
        setIsProcessing(false);
        setStatusMessage("");
        return;
      }

      setStatusMessage(`Sending ${parsedRecords.length} records to AI for cleanup... This may take 30-60 seconds.`);

      // Send to edge function for AI cleanup
      const { data, error } = await supabase.functions.invoke('cleanup-mailing-data', {
        body: { records: parsedRecords }
      });

      if (error) throw error;

      setCleanedData(data.cleaned_records || []);
      setStatusMessage(`Done! ${data.cleaned_records?.length || 0} records cleaned. Review them below.`);
      
      toast({
        title: "Data Cleaned!",
        description: `Processed ${data.cleaned_records.length} records with AI assistance`,
      });

      // Scroll to results
      setTimeout(() => {
        document.getElementById('cleaned-results')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('Cleanup error:', error);
      setStatusMessage("Cleanup failed — see error details below.");
      toast({
        title: "Cleanup Failed",
        description: error instanceof Error ? error.message : "Failed to clean data. The edge function may have timed out — try a smaller batch.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const exportToCSV = () => {
    if (cleanedData.length === 0) return;

    const headers = ['Tax ID', 'Date Recorded', 'Instrument Type', 'County', 'First Name', 'Last Name',
                     'Seller Address', 'Seller City', 'Seller State', 'Seller Zip'];
    
    const csvContent = [
      headers.join(','),
      ...cleanedData.map(record => [
        record.tax_id,
        record.date_recorded,
        record.instrument_type,
        record.county,
        `"${record.first_name}"`,
        `"${record.last_name}"`,
        `"${record.seller_address}"`,
        record.seller_city,
        record.seller_state,
        record.seller_zip
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cleaned-mailing-list-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast({
      title: "Export Successful",
      description: "Your cleaned data has been downloaded",
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Data Cleanup Tool</h1>
        <p className="text-muted-foreground mt-2">
          Paste messy spreadsheet data and let AI clean it up for Melissa Data
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Paste Raw Data</CardTitle>
          <CardDescription>
            Copy and paste data directly from Excel or Google Sheets (tab-delimited format will be preserved)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            placeholder="Paste your tab-delimited data here..."
            value={rawData}
            onChange={(e) => setRawData(e.target.value)}
            className="min-h-[200px] font-mono text-sm"
          />
          
          <div className="flex gap-2">
            <Button 
              onClick={cleanupData} 
              disabled={isProcessing || !rawData.trim()}
              className="flex-1"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Clean Up Data with AI
                </>
              )}
            </Button>
          </div>

          {statusMessage && (
            <div className={`text-sm p-3 rounded-md ${cleanedData.length > 0 ? 'bg-green-500/10 text-green-700 dark:text-green-400' : isProcessing ? 'bg-blue-500/10 text-blue-700 dark:text-blue-400' : 'bg-destructive/10 text-destructive'}`}>
              {isProcessing && <Loader2 className="inline mr-2 h-3 w-3 animate-spin" />}
              {statusMessage}
            </div>
          )}
        </CardContent>
      </Card>

      {cleanedData.length > 0 && (
        <Card id="cleaned-results">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Cleaned Data ({cleanedData.length} records)</CardTitle>
                <CardDescription>
                  Review the cleaned data below. You can edit any field before exporting.
                </CardDescription>
              </div>
              <Button onClick={exportToCSV} variant="outline">
                <Download className="mr-2 h-4 w-4" />
                Export to CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <DataCleanupTable 
              data={cleanedData} 
              onDataChange={setCleanedData}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
